import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import ReactSelect from "react-select";
import { FaExclamationTriangle, FaEye, FaTimes, FaSpinner, FaHistory } from "react-icons/fa";
import { Dialog, DialogHeader, DialogBody, DialogFooter, Button } from "@material-tailwind/react";
import { useAuth } from "../AuthContext";
import { rootUrl } from "../../../config/config";

export default function ExpenseGLPosting() {
    const { user, token } = useAuth();
    const [activeTab, setActiveTab] = useState("validate");

    // Week filtering state
    const [weeks, setWeeks] = useState([]);
    const [selectedWeek, setSelectedWeek] = useState(null);

    // Validation tab state
    const [approvedExpenses, setApprovedExpenses] = useState([]);
    const [validationResults, setValidationResults] = useState(null);
    const [selectedExpenses, setSelectedExpenses] = useState([]);
    const [loadingValidate, setLoadingValidate] = useState(false);

    // History tab state
    const [postingHistory, setPostingHistory] = useState([]);
    const [, setLoadingHistory] = useState(false);
    const [historyFilters, setHistoryFilters] = useState({ dateFrom: "", dateTo: "", status: "all" });

    // UI state
    const [toast, setToast] = useState(null);
    const [posting, setPosting] = useState(false);
    const [reversing, setReversing] = useState(null);
    const [showIneligible, setShowIneligible] = useState(true);
    const [postingDate, setPostingDate] = useState(new Date().toISOString().split('T')[0]);

    // Batch details state
    const [viewingBatch, setViewBatch] = useState(null);
    const [batchExpenses, setBatchExpenses] = useState([]);
    const [loadingDetails, setLoadingDetails] = useState(false);

    // Helper to format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);
    };

    const fetchBatchDetails = async (batchId) => {
        setLoadingDetails(true);
        // Open dialog immediately to show loader
        setViewBatch({ id: batchId, batch_reference: 'Loading...' });
        try {
            const response = await axios.get(`${rootUrl}/api/expenses/gl/postings/${batchId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.data.success) {
                setBatchExpenses(response.data.expenses || []);
                setViewBatch(response.data.batch);
            }
        } catch (error) {
            console.error("Error fetching batch details:", error);
            setToast({ type: "error", message: "Failed to load details", duration: 3000 });
            setViewBatch(null);
        } finally {
            setLoadingDetails(false);
        }
    };

    const fetchApprovedExpenses = useCallback(async () => {
        try {
            const params = { approval_status: 'APPROVED', gl_posted: false };
            if (selectedWeek) params.week_key = selectedWeek.value;

            const response = await axios.get(`${rootUrl}/api/expenses`, {
                params,
                headers: { Authorization: `Bearer ${token}` },
            });
            setApprovedExpenses(response.data.data || []);
        } catch (error) {
            console.error("Error fetching approved expenses:", error);
            setToast({ type: "error", message: "Failed to load approved expenses", duration: 3000 });
        }
    }, [token, selectedWeek]);

    // Fetch weeks separately
    useEffect(() => {
        const fetchWeeks = async () => {
            try {
                const response = await axios.get(`${rootUrl}/system/valid_weeks?past=24`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.data.success) {
                    setWeeks(response.data.valid_weeks.map(w => ({ value: w, label: w })));
                }
            } catch (error) {
                console.error("Error fetching weeks:", error);
            }
        };

        if (token) {
            fetchWeeks();
        }
    }, [token]);

    // Fetch approved expenses
    useEffect(() => {
        if (activeTab === "validate") {
            fetchApprovedExpenses();
        }
    }, [activeTab, fetchApprovedExpenses]);

    // Auto-validate if there are approved expenses and we haven't validated yet
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (activeTab === "validate" && approvedExpenses.length > 0 && !validationResults && !loadingValidate) {
            // Only auto-validate if there are unposted expenses
            const unposted = approvedExpenses.filter(e => !e.gl_posted);
            if (unposted.length > 0) {
                validateExpenses();
            }
        }
    }, [activeTab, approvedExpenses, validationResults, loadingValidate]);

    // Load posting history callback
    const loadPostingHistory = useCallback(async () => {
        setLoadingHistory(true);
        try {
            const params = { ...historyFilters };
            if (params.status === "all") delete params.status;

            const response = await axios.get(`${rootUrl}/api/expenses/gl/postings`, {
                params,
                headers: { Authorization: `Bearer ${token}` },
            });
            setPostingHistory(response.data.data || []);
        } catch (error) {
            console.error("Error fetching posting history:", error);
            setToast({ type: "error", message: "Failed to load posting history", duration: 3000 });
        } finally {
            setLoadingHistory(false);
        }
    }, [historyFilters, token]);

    // Fetch posting history
    useEffect(() => {
        if (activeTab === "history") {
            loadPostingHistory();
        }
    }, [activeTab, loadPostingHistory]);

    const validateExpenses = async () => {
        if (approvedExpenses.length === 0) {
            setToast({ type: "warning", message: "No approved expenses to validate", duration: 3000 });
            return;
        }

        setLoadingValidate(true);
        try {
            const expenseIds = approvedExpenses.map((e) => e.id);
            const response = await axios.post(
                `${rootUrl}/api/expenses/gl/validate`,
                { expenseIds },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setValidationResults(response.data);
            setSelectedExpenses(
                response.data.eligible.map((e) => ({
                    ...e,
                    selected: !e.gl_posted, // Only auto-select if not already posted
                }))
            );
        } catch (error) {
            console.error("Error validating expenses:", error);
            setToast({ type: "error", message: "Validation failed", duration: 3000 });
        } finally {
            setLoadingValidate(false);
        }
    };

    const postToGL = async () => {
        const eligibleToPost = selectedExpenses.filter((e) => e.selected && !e.gl_posted);

        if (eligibleToPost.length === 0) {
            setToast({ type: "warning", message: "Please select at least one unposted expense to post", duration: 3000 });
            return;
        }

        setPosting(true);
        try {
            const expenseIds = eligibleToPost.map((e) => e.id);

            const response = await axios.post(
                `${rootUrl}/api/expenses/gl/post-batch`,
                { expenseIds, postedBy: user.username, postingDate },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setToast({
                type: "success",
                message: `Posted ${response.data.expenseCount} expenses. Batch: ${response.data.batchReference}`,
                duration: 5000,
            });

            // Reset and reload fresh data
            setValidationResults(null);
            setSelectedExpenses([]);
            fetchApprovedExpenses(); // Refresh the validate grid
            
            // Navigate to history and refresh it
            setActiveTab("history");
            setTimeout(loadPostingHistory, 500);
        } catch (error) {
            console.error("Error posting to GL:", error);
            setToast({ type: "error", message: error.response?.data?.message || "Failed to post to GL", duration: 3000 });
        } finally {
            setPosting(false);
        }
    };

    const reversePosting = async (batchId) => {
        if (!window.confirm("Are you sure you want to reverse this posting? This action cannot be undone.")) {
            return;
        }

        setReversing(batchId);
        try {
            await axios.post(
                `${rootUrl}/api/expenses/gl/postings/${batchId}/reverse`,
                { reversedBy: user.username, reason: "Manual reversal" },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            setToast({ type: "success", message: "Posting reversed successfully", duration: 5000 });
            loadPostingHistory();
        } catch (error) {
            console.error("Error reversing posting:", error);
            setToast({ type: "error", message: "Failed to reverse posting", duration: 3000 });
        } finally {
            setReversing(null);
        }
    };

    const toggleExpenseSelection = (expenseId) => {
        setSelectedExpenses(
            selectedExpenses.map((e) =>
                e.id === expenseId ? { ...e, selected: !e.selected } : e
            )
        );
    };

    return (
        <div className="min-h-screen bg-gray-25">
            <div className="p-6 lg:p-8">
                <div className="text-center mb-8">
                    <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">GL Posting Manager</h1>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full mb-4"></div>
                    <p className="text-lg text-[#2a2e30] opacity-80">Validate and post expenses to General Ledger</p>
                </div>
            </div>

            <div className="w-full max-w-7xl mx-auto px-2 sm:px-4 pb-8">
                {/* Toast Notification */}
                {toast && (
                    <div
                        className={`mb-4 px-4 py-3 rounded-lg text-sm font-semibold flex items-center justify-between shadow-sm animate-fade-in ${toast.type === "success"
                                ? "bg-green-50 text-green-700 border border-green-200"
                                : toast.type === "warning"
                                    ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
                                    : "bg-red-50 text-red-700 border border-red-200"
                            }`}
                    >
                        <span>{toast.message}</span>
                        <button onClick={() => setToast(null)} className="text-xs font-bold px-2 py-1 rounded hover:bg-black/5">Dismiss</button>
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 mb-6">
                    <div className="flex border-b border-gray-100 mb-6">
                        <button
                            onClick={() => setActiveTab("validate")}
                            className={`px-6 py-3 font-bold text-sm transition-all border-b-2 ${activeTab === "validate"
                                ? "border-[#FF5733] text-[#FF5733]"
                                : "border-transparent text-gray-400 hover:text-gray-600"
                                }`}
                        >
                            Validate & Post
                        </button>
                        <button
                            onClick={() => setActiveTab("history")}
                            className={`px-6 py-3 font-bold text-sm transition-all border-b-2 ${activeTab === "history"
                                ? "border-[#FF5733] text-[#FF5733]"
                                : "border-transparent text-gray-400 hover:text-gray-600"
                                }`}
                        >
                            Posting History
                        </button>
                    </div>

                    {/* Validate & Post Tab */}
                    {activeTab === "validate" && (
                        <div className="space-y-6">
                            <div className="flex flex-col md:flex-row items-end gap-4 bg-gray-50/50 p-5 rounded-2xl border border-gray-100">
                                <div className="w-full md:w-64">
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Filter by System Week</label>
                                    <ReactSelect
                                        options={[{ value: null, label: "All Weeks" }, ...weeks]}
                                        value={selectedWeek || { value: null, label: "All Weeks" }}
                                        onChange={(option) => {
                                            setSelectedWeek(option && option.value ? option : null);
                                            setValidationResults(null);
                                        }}
                                        placeholder="Select Week..."
                                        isClearable
                                        styles={{
                                            control: (base) => ({ ...base, borderRadius: '12px', border: '1px solid #e5e7eb', padding: '2px' })
                                        }}
                                    />
                                </div>
                                <div className="text-xs text-gray-500 mb-2 italic bg-white px-3 py-1.5 rounded-full border border-gray-100 font-medium">
                                    Showing unposted approved expenses
                                </div>
                            </div>

                            {!validationResults ? (
                                <div className="text-center py-16 bg-gray-50/30 rounded-3xl border-2 border-dashed border-gray-100">
                                    <div className="bg-white p-4 rounded-2xl shadow-sm inline-block mb-4">
                                        <FaExclamationTriangle className="text-orange-400" size={32} />
                                    </div>
                                    <p className="text-gray-600 font-bold mb-6">
                                        {approvedExpenses.length === 0
                                            ? (selectedWeek ? `No approved expenses found for week ${selectedWeek.value}` : "No approved expenses found")
                                            : `Found ${approvedExpenses.length} approved expenses ready for GL posting ${selectedWeek ? `for week ${selectedWeek.value}` : ""}`}
                                    </p>
                                    <button
                                        onClick={validateExpenses}
                                        disabled={approvedExpenses.length === 0 || loadingValidate}
                                        className="bg-[#FF5733] hover:bg-[#e64520] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-bold rounded-xl px-8 py-3 transition-all shadow-lg shadow-orange-200"
                                    >
                                        {loadingValidate ? <FaSpinner className="animate-spin" /> : "Validate Selection"}
                                    </button>
                                </div>
                            ) : (
                                <div className="animate-fade-in">
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                                        <div className="bg-green-50 border border-green-100 rounded-2xl p-5">
                                            <p className="text-green-600 text-[10px] font-black uppercase tracking-widest mb-1">Eligible to Post</p>
                                            <p className="text-3xl font-black text-green-700">{validationResults.eligible.length}</p>
                                        </div>
                                        <div className="bg-red-50 border border-red-100 rounded-2xl p-5">
                                            <p className="text-red-600 text-[10px] font-black uppercase tracking-widest mb-1">Failed Validation</p>
                                            <p className="text-3xl font-black text-red-700">{validationResults.ineligible.length}</p>
                                        </div>
                                        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
                                            <p className="text-blue-600 text-[10px] font-black uppercase tracking-widest mb-1">Total Selected</p>
                                            <p className="text-3xl font-black text-blue-700">{validationResults.summary?.total || 0}</p>
                                        </div>
                                    </div>

                                    {validationResults.eligible.length > 0 && (
                                        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm mb-8">
                                            <div className="bg-gray-50/50 px-6 py-4 border-b border-gray-100 flex justify-between items-center">
                                                <h3 className="font-bold text-gray-800">Review Eligible Expenses</h3>
                                                <button onClick={() => setValidationResults(null)} className="text-[10px] font-black text-gray-400 uppercase hover:text-red-500 transition-colors">Change Selection</button>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-sm">
                                                    <thead className="bg-gray-25 text-[10px] font-black text-gray-400 uppercase border-b border-gray-100">
                                                        <tr>
                                                            <th className="px-6 py-4 text-left">
                                                                <input type="checkbox" checked={selectedExpenses.every(e => e.selected)} onChange={e => setSelectedExpenses(selectedExpenses.map(exp => ({ ...exp, selected: e.target.checked })))} className="rounded" />
                                                            </th>
                                                            <th className="px-6 py-4 text-left">Exp #</th>
                                                            <th className="px-6 py-4 text-left">Date & Week</th>
                                                            <th className="px-6 py-4 text-left">Category</th>
                                                            <th className="px-6 py-4 text-right">Amount</th>
                                                            <th className="px-6 py-4 text-left">Account Mapping</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-50">
                                                        {selectedExpenses.map((expense) => (
                                                            <tr key={expense.id} className={`hover:bg-gray-25 transition-colors group ${expense.gl_posted ? 'opacity-70' : ''}`}>
                                                                <td className="px-6 py-4">
                                                                    <input 
                                                                        type="checkbox" 
                                                                        checked={expense.selected} 
                                                                        onChange={() => !expense.gl_posted && toggleExpenseSelection(expense.id)} 
                                                                        disabled={expense.gl_posted}
                                                                        className={`rounded ${expense.gl_posted ? 'cursor-not-allowed text-gray-300' : ''}`} 
                                                                    />
                                                                </td>
                                                                <td className="px-6 py-4 font-bold text-gray-700 font-mono text-xs">
                                                                    <div className="flex flex-col gap-1">
                                                                        <span>{expense.expense_no}</span>
                                                                        {expense.gl_posted && (
                                                                            <span className="inline-block px-2 py-0.5 bg-green-100 text-green-700 text-[8px] font-black rounded uppercase tracking-tighter w-max">Synchronized</span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <div className="flex flex-col">
                                                                        <span className="font-bold text-gray-700">{new Date(expense.expense_date).toLocaleDateString()}</span>
                                                                        <span className="text-[10px] text-gray-400 font-mono">{expense.week_key}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <div className="flex flex-col">
                                                                        <span className="font-bold text-gray-700">{expense.category_name}</span>
                                                                        <span className="text-[10px] text-gray-400 italic">{expense.subcategory_name || '-'}</span>
                                                                    </div>
                                                                </td>
                                                                <td className="px-6 py-4 text-right font-black text-gray-900">{formatCurrency(expense.amount)}</td>
                                                                <td className="px-6 py-4">
                                                                    <div className="flex items-center gap-2">
                                                                        <div className="px-2 py-1 bg-blue-50 text-blue-700 text-[10px] font-black rounded uppercase">Dr: {expense.gl_debit_account}</div>
                                                                        <div className="px-2 py-1 bg-green-50 text-green-700 text-[10px] font-black rounded uppercase">Cr: {expense.gl_credit_account}</div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="bg-gray-50/50 border-t border-gray-100">
                                                        <tr>
                                                            <td colSpan="4" className="px-6 py-4 text-right text-[10px] font-black text-gray-400 uppercase">Selected Batch Total:</td>
                                                            <td className="px-6 py-4 text-right text-lg font-black text-[#FF5733]">
                                                                {formatCurrency(selectedExpenses.filter(e => e.selected && !e.gl_posted).reduce((sum, e) => sum + (parseFloat(e.amount) || 0), 0))}
                                                            </td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {/* Ineligible List */}
                                    {validationResults.ineligible.length > 0 && (
                                        <div className="bg-red-50/50 border border-red-100 rounded-2xl p-6 mb-8">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex items-center gap-3">
                                                    <FaExclamationTriangle className="text-red-500" />
                                                    <h3 className="font-bold text-red-900">{validationResults.ineligible.length} Items Require Attention</h3>
                                                </div>
                                                <button onClick={() => setShowIneligible(s => !s)} className="text-[10px] font-black text-red-400 uppercase tracking-widest">{showIneligible ? 'Collapse' : 'Expand'}</button>
                                            </div>
                                            {showIneligible && (
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-96 overflow-y-auto pr-2">
                                                    {validationResults.ineligible.map((expense, idx) => (
                                                        <div key={idx} className="bg-white border border-red-100 p-4 rounded-xl shadow-sm">
                                                            <div className="flex justify-between items-center mb-2 border-b border-red-50 pb-2">
                                                                <span className="font-mono font-bold text-red-700 text-xs">{expense.expense_no}</span>
                                                                <span className="text-[8px] bg-red-100 text-red-700 px-2 py-0.5 rounded font-black uppercase">Blocked</span>
                                                            </div>
                                                            <ul className="space-y-1">
                                                                {(expense.issues || []).map((issue, i) => (
                                                                    <li key={i} className="text-xs text-red-600 font-medium leading-tight">&#8226; {issue}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    <div className="flex flex-col md:flex-row justify-end items-center gap-6 pt-6 border-t border-gray-100">
                                        <div className="flex flex-col items-end">
                                            <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">GL Entry Date</label>
                                            <input 
                                                type="date" 
                                                value={postingDate}
                                                onChange={(e) => setPostingDate(e.target.value)}
                                                className="px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-[#FF5733] outline-none font-bold text-gray-700"
                                            />
                                        </div>
                                        <div className="flex gap-4">
                                            <button onClick={() => setValidationResults(null)} className="px-8 py-3 text-sm font-bold text-gray-400 hover:text-gray-600 transition-colors uppercase tracking-widest">Discard</button>
                                            <button
                                                onClick={postToGL}
                                                disabled={selectedExpenses.filter((e) => e.selected && !e.gl_posted).length === 0 || posting}
                                                className="bg-gray-900 hover:bg-black disabled:opacity-50 text-white text-sm font-bold rounded-xl px-10 py-3 shadow-xl transition-all uppercase tracking-widest"
                                            >
                                                {posting ? "Processing..." : `Commit to General Ledger`}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* History Tab */}
                    {activeTab === "history" && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50/50 p-5 rounded-2xl border border-gray-100 items-end">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Date From</label>
                                    <input type="date" value={historyFilters.dateFrom} onChange={e => setHistoryFilters({ ...historyFilters, dateFrom: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-[#FF5733] outline-none" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Date To</label>
                                    <input type="date" value={historyFilters.dateTo} onChange={e => setHistoryFilters({ ...historyFilters, dateTo: e.target.value })} className="w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-[#FF5733] outline-none" />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2 ml-1">Status</label>
                                    <ReactSelect
                                        options={[{ value: "all", label: "All Batches" }, { value: "POSTED", label: "Posted" }, { value: "REVERSED", label: "Reversed" }]}
                                        value={{ value: historyFilters.status, label: historyFilters.status === "all" ? "All Batches" : historyFilters.status }}
                                        onChange={option => setHistoryFilters({ ...historyFilters, status: option.value })}
                                        isSearchable={false}
                                        styles={{ control: (base) => ({ ...base, borderRadius: '12px', border: '1px solid #e5e7eb', padding: '2px' }) }}
                                    />
                                </div>
                                <button onClick={loadPostingHistory} className="bg-white border border-gray-200 hover:border-[#FF5733] hover:text-[#FF5733] text-gray-600 px-6 py-2.5 rounded-xl text-sm font-bold transition-all shadow-sm">Filter Results</button>
                            </div>

                            {postingHistory.length > 0 ? (
                                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm">
                                            <thead className="bg-gray-25 text-[10px] font-black text-gray-400 uppercase border-b border-gray-100">
                                                <tr>
                                                    <th className="px-6 py-4 text-left">Batch Reference</th>
                                                    <th className="px-6 py-4 text-left">Posting Date</th>
                                                    <th className="px-6 py-4 text-center">Items</th>
                                                    <th className="px-6 py-4 text-right">Batch Total</th>
                                                    <th className="px-6 py-4 text-left">User</th>
                                                    <th className="px-6 py-4 text-center">Status</th>
                                                    <th className="px-6 py-4 text-center">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-50">
                                                {postingHistory.map((batch) => (
                                                    <tr key={batch.id} className="hover:bg-gray-25 transition-colors group cursor-pointer" onClick={() => fetchBatchDetails(batch.id)}>
                                                        <td className="px-6 py-4 font-bold text-gray-700 font-mono tracking-tight">{batch.batch_reference}</td>
                                                        <td className="px-6 py-4 text-gray-500 font-medium">{new Date(batch.posted_date).toLocaleDateString()} <span className="text-[10px] text-gray-300 ml-1">{new Date(batch.posted_date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span></td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className="bg-gray-100 px-2 py-1 rounded text-xs font-bold text-gray-600">{batch.expense_count}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-right font-black text-gray-900">
                                                            {formatCurrency(batch.total_debit)}
                                                        </td>
                                                        <td className="px-6 py-4 text-gray-600 font-medium uppercase text-[10px]">{batch.posted_by}</td>
                                                        <td className="px-6 py-4 text-center">
                                                            <span className={`px-2 py-1 rounded text-[10px] font-black uppercase tracking-tight ${batch.status === "POSTED" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                                                                {batch.status}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-center">
                                                            <div className="flex items-center justify-center gap-3">
                                                                <button onClick={(e) => { e.stopPropagation(); fetchBatchDetails(batch.id); }} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all" title="View Details"><FaEye /></button>
                                                                {batch.status === "POSTED" && (
                                                                    <button onClick={(e) => { e.stopPropagation(); reversePosting(batch.id); }} disabled={reversing === batch.id} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all" title="Reverse Posting"><FaHistory /></button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-20 bg-gray-50/30 rounded-3xl border-2 border-dashed border-gray-100">
                                    <p className="text-gray-400 font-bold tracking-widest uppercase text-xs">No Posting Records Found</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Batch Details Dialog */}
            <Dialog open={!!viewingBatch} handler={() => setViewBatch(null)} size="lg" className="rounded-3xl shadow-2xl overflow-hidden bg-white">
                <DialogHeader className="bg-gray-50/50 border-b border-gray-100 flex items-center justify-between p-6">
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <h2 className="text-xl font-black text-gray-900 tracking-tight">Batch Details</h2>
                            <span className="px-2 py-1 bg-gray-900 text-white text-[10px] font-black rounded-lg uppercase font-mono tracking-widest">{viewingBatch?.batch_reference}</span>
                        </div>
                        <p className="text-xs text-gray-400 font-medium">Breakdown of individual expenses in this posting</p>
                    </div>
                    <button onClick={() => setViewBatch(null)} className="p-2 hover:bg-white rounded-full text-gray-400 hover:text-gray-600 transition-all shadow-sm"><FaTimes /></button>
                </DialogHeader>
                <DialogBody className="p-0 overflow-y-auto max-h-[65vh]">
                    {loadingDetails ? (
                        <div className="py-20 flex flex-col items-center justify-center gap-4">
                            <FaSpinner className="animate-spin text-[#FF5733] text-3xl" />
                            <span className="text-sm font-bold text-gray-400 uppercase tracking-widest">Reconstructing Batch...</span>
                        </div>
                    ) : (
                        <table className="w-full text-sm">
                            <thead className="bg-gray-25 text-[10px] font-black text-gray-400 uppercase border-b border-gray-100 sticky top-0 z-10">
                                <tr>
                                    <th className="px-6 py-4 text-left">Exp #</th>
                                    <th className="px-6 py-4 text-left">Date</th>
                                    <th className="px-6 py-4 text-left">Category</th>
                                    <th className="px-6 py-4 text-left">Plate / Account</th>
                                    <th className="px-6 py-4 text-right">Amount (KES)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-50 bg-white">
                                {batchExpenses.map((expense) => (
                                    <tr key={expense.id} className="hover:bg-gray-25 transition-colors">
                                        <td className="px-6 py-4 font-mono font-bold text-gray-700 text-xs">{expense.expense_no}</td>
                                        <td className="px-6 py-4 text-gray-500 font-medium">{new Date(expense.expense_date).toLocaleDateString()}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-bold text-gray-700">{expense.category_name}</span>
                                                <span className="text-[10px] text-gray-400 italic">{expense.subcategory_name || '-'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex flex-col">
                                                <span className="font-black text-gray-900 text-xs tracking-tighter">{expense.plate_no || 'OFFICE'}</span>
                                                <span className="text-[10px] text-gray-400 font-medium uppercase">{expense.bank_name || 'Cash'}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-right font-black text-gray-900">{formatCurrency(expense.amount)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </DialogBody>
                <DialogFooter className="bg-gray-50/50 border-t border-gray-100 flex items-center justify-between p-6">
                    <div className="flex flex-col">
                        <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Aggregate Total</span>
                        <span className="text-xl font-black text-[#FF5733]">{formatCurrency(viewingBatch?.total_debit)}</span>
                    </div>
                    <Button variant="text" onClick={() => setViewBatch(null)} className="px-8 py-3 rounded-xl font-black text-sm text-gray-400 uppercase tracking-widest hover:bg-white transition-all">Close</Button>
                </DialogFooter>
            </Dialog>
        </div>
    );
}
